import { Renderer } from '@/rendering/renderer';
import { renderTerrain, getGroundY } from '@/game/terrain';
import { hexToColor, type Color } from '@/types';
import type { EditorStore } from './editor-store';
import type { LevelData } from '@/game/level';
import type { PaletteTool } from './editor-toolbar';
import { haptic } from './editor-haptic';

/**
 * Pure screen-to-world mapping, factored out for testability.
 *
 * Click coordinates are in CSS pixels (relative to the viewport); the canvas
 * backing store is `canvas.width = rect.width * devicePixelRatio`. The camera's
 * world viewport spans `canvas.width / zoom` world units horizontally, so the
 * click's fractional position across `rect.width` must be scaled by the world
 * viewport width — NOT divided by zoom alone (that ignores DPR and lands clicks
 * to the left of the cursor on high-DPR displays).
 */
export interface ScreenToWorldInputs {
  rectLeft: number; rectTop: number;
  rectWidth: number; rectHeight: number;
  canvasWidth: number; canvasHeight: number;
  cameraX: number; cameraY: number;
  zoom: number;
}
export function mapScreenToWorld(
  screenX: number, screenY: number, i: ScreenToWorldInputs,
): { x: number; y: number } {
  const fracX = (screenX - i.rectLeft) / i.rectWidth;
  const fracY = (screenY - i.rectTop) / i.rectHeight;
  const worldWidth = i.canvasWidth / i.zoom;
  const worldHeight = i.canvasHeight / i.zoom;
  return {
    x: i.cameraX + fracX * worldWidth,
    y: i.cameraY + worldHeight * (0.5 - fracY),
  };
}

/** Editor's preview pane. Renders LevelData to an owned canvas. */
export class EditorPreview {
  private canvas: HTMLCanvasElement;
  private renderer: Renderer;
  private store: EditorStore;
  private raf = 0;

  /** World X at the left edge of the preview viewport. */
  private cameraX = 0;
  /** Pixels-per-world-unit. 1 = 1:1 world units. */
  private zoom = 1;

  private playheadX = 0;
  private pointers = new Map<number, { x: number; y: number }>();
  private panStart = { cameraX: 0, pointerX: 0 };
  private pinchStart = { zoom: 1, distance: 0 };
  private draggingPlayhead = false;
  private currentTool: PaletteTool = null;

  onPlayheadChange: ((pos: number) => void) | null = null;
  onWorldTap: ((wx: number, wy: number) => void) | null = null;
  onWorldSelect: ((wx: number, wy: number) => void) | null = null;

  constructor(host: HTMLElement, store: EditorStore) {
    this.store = store;
    this.canvas = document.createElement('canvas');
    this.canvas.style.display = 'block';
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    host.appendChild(this.canvas);
    this.renderer = new Renderer(this.canvas);
    this.renderer.bloomStrength = 0.5;
    this.renderer.vignetteIntensity = 0.25;

    store.on('level-changed', () => { /* next frame picks it up */ });

    this.loop = this.loop.bind(this);
    this.raf = requestAnimationFrame(this.loop);

    this.canvas.addEventListener('pointerdown', (e) => this.onPointerDown(e));
    this.canvas.addEventListener('pointermove', (e) => this.onPointerMove(e));
    this.canvas.addEventListener('pointerup', (e) => this.onPointerUp(e));
    this.canvas.addEventListener('pointercancel', (e) => this.onPointerUp(e));
    this.canvas.style.touchAction = 'none';
  }

  destroy(): void {
    cancelAnimationFrame(this.raf);
    // Release the WebGL context immediately. Without this, the canvas only
    // gets GC'd lazily, and rapid editor↔play-test cycles exhaust the
    // browser's per-process WebGL context pool, forcing loss of older
    // contexts (observed as a white canvas).
    this.renderer.dispose();
    this.canvas.remove();
  }

  setCamera(worldX: number, zoom = this.zoom): void {
    this.cameraX = Math.max(0, worldX);
    this.zoom = Math.max(0.25, Math.min(4, zoom));
  }

  /** World-length of the preview's visible viewport. */
  get viewWidth(): number { return this.renderer.camera.viewWidth / this.zoom; }

  setTool(t: PaletteTool): void {
    this.currentTool = t;
  }

  private loop(): void {
    this.render();
    this.raf = requestAnimationFrame(this.loop);
  }

  private render(): void {
    const lvl = this.store.level;
    const r = this.renderer;
    const cam = r.camera;

    cam.position.x = this.cameraX + this.viewWidth / 2;
    cam.position.y = 150; // rough visual center for ℝ mode
    cam.leadOffset.x = 0;
    cam.viewWidth = r.canvas.width / this.zoom;
    cam.viewHeight = r.canvas.height / this.zoom;

    const base = hexToColor(lvl.settings.baseColor);
    const accent = hexToColor(lvl.settings.accentColor);

    r.beginFrame();

    // Background grid
    const gridY = lvl.segments[0] ? getGroundY(
      lvl.segments, this.segmentOffsets(lvl), this.cameraX, 80,
    ) : 80;
    r.drawGrid(gridY, base, 50, 0.4);

    // Terrain
    // renderTerrain uses: worldX = screenX + scrollOffset - respawnX
    // With scrollOffset=cameraX and respawnX=cameraX, worldX = screenX
    // giving screen coords matching world coords in the camera's space.
    if (lvl.segments.length > 0) {
      renderTerrain(
        r, lvl.segments, this.segmentOffsets(lvl),
        this.cameraX, this.cameraX,
        this.cameraX, this.cameraX + this.viewWidth,
        80, base,
      );
    }

    // Ceiling line(s) — drawn as a sampled polyline across each segment's local space
    if (lvl.segments.length > 0) {
      this.renderCeilings(lvl, base);
    }

    // Segment boundary markers — thin vertical lines where segments meet
    const offsets = this.segmentOffsets(lvl);
    for (let i = 1; i < offsets.length; i++) {
      const x = offsets[i];
      if (x < this.cameraX || x > this.cameraX + this.viewWidth) continue;
      r.drawRect(x - 0.5, 0, 1, 500, { r: 0.4, g: 0.4, b: 0.6, a: 0.5 });
    }

    // Schematic for complex segments — full 3D preview comes in Tier 3b
    for (let i = 0; i < lvl.segments.length; i++) {
      const seg = lvl.segments[i];
      if (seg.mode !== 'complex') continue;
      const offs = this.segmentOffsets(lvl);
      this.drawComplexSchematic(seg, offs[i]);
    }

    // Obstacles
    const sel = this.store.selection;
    for (let si = 0; si < lvl.segments.length; si++) {
      const seg = lvl.segments[si];
      const baseX = this.segmentOffsets(lvl)[si];
      for (let i = 0; i < seg.obstacles.length; i++) {
        const obs = seg.obstacles[i];
        const wx = baseX + obs.position.along;
        const isSel = sel.kind === 'obstacle' && sel.segmentId === seg.id && sel.index === i;
        this.drawObstaclePreview(wx, obs, base, accent, isSel);
      }
    }

    // Collectibles
    for (const col of lvl.collectibles) {
      this.drawCollectiblePreview(col);
    }

    this.drawTriggers(lvl);
    this.drawPlayhead();
    r.endFrame();
  }

  private segmentOffsets(lvl: LevelData): number[] {
    const offsets: number[] = [];
    let o = 0;
    for (const s of lvl.segments) { offsets.push(o); o += s.length; }
    return offsets;
  }

  private drawObstaclePreview(
    wx: number,
    obs: LevelData['segments'][0]['obstacles'][0],
    _base: Color,
    _accent: Color,
    selected: boolean,
  ): void {
    const r = this.renderer;
    const baseY = 80 + obs.position.up;
    const w = obs.size.width;
    const h = obs.size.height;

    if (obs.type === 'spike') {
      const color = { r: 1, g: 0.2, b: 0.4, a: 1 };
      r.drawTriangle(wx, baseY, wx + w, baseY, wx + w / 2, baseY + h, color);
    } else if (obs.type === 'wall') {
      const color = { r: 1, g: 0.4, b: 0, a: 0.6 };
      r.drawRect(wx, baseY, w, h, color);
    } else if (obs.type === 'moving') {
      const color = { r: 0.2, g: 0.8, b: 1, a: 0.6 };
      r.drawRect(wx, baseY, w, h, color);
      this.drawMotionEnvelope(wx, baseY, w, h, obs.behavior ?? null);
    } else {
      r.drawRect(wx, baseY, w, h, { r: 0.5, g: 0.5, b: 0.5, a: 0.5 });
    }

    if (selected) {
      const hi: Color = { r: 0, g: 1, b: 1, a: 1 };
      r.drawLine(wx - 2, baseY - 2, wx + w + 2, baseY - 2, 2, hi);
      r.drawLine(wx + w + 2, baseY - 2, wx + w + 2, baseY + h + 2, 2, hi);
      r.drawLine(wx + w + 2, baseY + h + 2, wx - 2, baseY + h + 2, 2, hi);
      r.drawLine(wx - 2, baseY + h + 2, wx - 2, baseY - 2, 2, hi);
    }
  }

  private drawMotionEnvelope(
    wx: number, baseY: number, w: number, h: number,
    behavior: { motion: string; amplitude: number; frequency: number; phase: number } | null,
  ): void {
    if (!behavior) return;
    const r = this.renderer;
    const cx = wx + w / 2;
    const cy = baseY + h / 2;
    const amp = behavior.amplitude;
    const ghost: Color = { r: 0.2, g: 0.8, b: 1, a: 0.25 };

    switch (behavior.motion) {
      case 'sine': {
        r.drawRect(wx, baseY + amp, w, h, ghost);
        r.drawRect(wx, baseY - amp, w, h, ghost);
        r.drawLine(cx, cy - amp, cx, cy + amp, 1, { ...ghost, a: 0.5 });
        break;
      }
      case 'cosine': {
        r.drawRect(wx + amp, baseY, w, h, ghost);
        r.drawRect(wx - amp, baseY, w, h, ghost);
        r.drawLine(cx - amp, cy, cx + amp, cy, 1, { ...ghost, a: 0.5 });
        break;
      }
      case 'circular': {
        const steps = 16;
        let px = cx + amp, py = cy;
        for (let i = 1; i <= steps; i++) {
          const a = (i / steps) * Math.PI * 2;
          const nx = cx + amp * Math.cos(a);
          const ny = cy + amp * Math.sin(a);
          r.drawLine(px, py, nx, ny, 1, { ...ghost, a: 0.5 });
          px = nx; py = ny;
        }
        break;
      }
    }
  }

  private drawCollectiblePreview(col: LevelData['collectibles'][0]): void {
    const r = this.renderer;
    const y = 80 + col.position.up;
    const x = col.position.along;
    if (col.type === 'star') {
      const c: Color = { r: 1, g: 1, b: 0.3, a: 1 };
      r.drawTriangle(x, y + 8, x - 5, y, x + 5, y, c);
      r.drawTriangle(x, y - 8, x - 5, y, x + 5, y, c);
    } else if (col.type === 'fragment') {
      const c: Color = { r: 0.3, g: 1, b: 1, a: 1 };
      const s = 6;
      r.drawTriangle(x, y + s, x - s, y - s / 2, x + s, y - s / 2, c);
      r.drawTriangle(x, y - s, x - s, y + s / 2, x + s, y + s / 2, c);
    } else if (col.type === 'powerup') {
      const c: Color = { r: 1, g: 0.3, b: 1, a: 1 };
      const s = 7;
      r.drawTriangle(x, y + s, x - s, y, x + s, y, c);
      r.drawTriangle(x, y - s, x - s, y, x + s, y, c);
    }
  }

  private drawComplexSchematic(seg: LevelData['segments'][0], segStart: number): void {
    const r = this.renderer;
    const viewLeft = Math.max(segStart, this.cameraX);
    const viewRight = Math.min(segStart + seg.length, this.cameraX + this.viewWidth);
    if (viewRight <= viewLeft) return;
    const bgColor: Color = { r: 0.3, g: 0.1, b: 0.4, a: 0.5 };
    r.drawRect(viewLeft, 60, viewRight - viewLeft, 140, bgColor);

    // Tunnel cross-section circle (rough approximation) at segment start if visible
    if (segStart >= this.cameraX && segStart <= this.cameraX + this.viewWidth) {
      const cx = segStart + 30;
      const cy = 130;
      const radius = (seg.terrain.type === 'tunnel' && (seg.terrain as { tunnel?: { radius?: number } }).tunnel?.radius)
        ? (seg.terrain as { tunnel?: { radius?: number } }).tunnel!.radius! * 0.3
        : 25;
      const lineColor: Color = { r: 0.8, g: 0.4, b: 1, a: 0.8 };
      const steps = 24;
      let px = cx + radius, py = cy;
      for (let k = 1; k <= steps; k++) {
        const a = (k / steps) * Math.PI * 2;
        const nx = cx + radius * Math.cos(a);
        const ny = cy + radius * Math.sin(a);
        r.drawLine(px, py, nx, ny, 1.5, lineColor);
        px = nx; py = ny;
      }
    }
  }

  private onPointerDown(e: PointerEvent): void {
    this.canvas.setPointerCapture(e.pointerId);
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const world = this.screenToWorld(e.clientX, e.clientY);
    // Near the playhead? (12px world tolerance)
    if (Math.abs(world.x - this.playheadX) < 12 && this.pointers.size === 1) {
      this.draggingPlayhead = true;
      return;
    }
    if (this.pointers.size === 1) {
      this.panStart = { cameraX: this.cameraX, pointerX: e.clientX };
    } else if (this.pointers.size === 2) {
      this.pinchStart.zoom = this.zoom;
      const pts = [...this.pointers.values()];
      this.pinchStart.distance = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
    }
  }

  private onPointerMove(e: PointerEvent): void {
    if (!this.pointers.has(e.pointerId)) return;
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (this.draggingPlayhead) {
      const world = this.screenToWorld(e.clientX, e.clientY);
      this.playheadX = Math.max(0, world.x);
      this.onPlayheadChange?.(this.playheadX);
      return;
    }

    if (this.pointers.size === 1) {
      const dxCss = e.clientX - this.panStart.pointerX;
      const rect = this.canvas.getBoundingClientRect();
      const worldWidth = this.renderer.canvas.width / this.zoom;
      const dxWorld = rect.width > 0 ? (dxCss / rect.width) * worldWidth : 0;
      this.cameraX = Math.max(0, this.panStart.cameraX - dxWorld);
    } else if (this.pointers.size === 2) {
      const pts = [...this.pointers.values()];
      const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
      if (this.pinchStart.distance > 0) {
        const factor = dist / this.pinchStart.distance;
        this.zoom = Math.max(0.25, Math.min(4, this.pinchStart.zoom * factor));
      }
    }
  }

  private onPointerUp(e: PointerEvent): void {
    const startX = this.panStart.pointerX;
    const moved = Math.abs(e.clientX - startX);
    const wasDraggingPlayhead = this.draggingPlayhead;
    this.pointers.delete(e.pointerId);
    if (this.pointers.size < 2) this.pinchStart.distance = 0;
    this.draggingPlayhead = false;
    if (wasDraggingPlayhead) return;
    if (moved < 4 && this.pointers.size === 0) {
      const w = this.screenToWorld(e.clientX, e.clientY);
      if (this.currentTool) { haptic('place'); this.onWorldTap?.(w.x, w.y); }
      else this.onWorldSelect?.(w.x, w.y);
    }
  }

  private drawTriggers(lvl: LevelData): void {
    const r = this.renderer;
    const sel = this.store.selection;
    for (const t of lvl.triggers) {
      if (t.position < this.cameraX || t.position > this.cameraX + this.viewWidth) continue;
      const color = triggerColor(t.type);
      const isSel = sel.kind === 'trigger' && sel.id === t.id;
      const alpha = isSel ? 1 : 0.7;
      r.drawRect(t.position - 1, 0, 2, 500, { ...color, a: alpha });
    }
  }

  /** Draw the playhead as a vertical bright line. Called in render(). */
  private drawPlayhead(): void {
    const r = this.renderer;
    r.drawRect(this.playheadX - 1, 0, 2, 500, { r: 0, g: 1, b: 1, a: 1 });
  }

  get playhead(): number { return this.playheadX; }
  setPlayhead(v: number): void { this.playheadX = Math.max(0, v); }

  /** Convert screen (canvas-relative) x,y to world coordinates. */
  screenToWorld(sx: number, sy: number): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    return mapScreenToWorld(sx, sy, {
      rectLeft: rect.left, rectTop: rect.top,
      rectWidth: rect.width, rectHeight: rect.height,
      canvasWidth: this.renderer.canvas.width,
      canvasHeight: this.renderer.canvas.height,
      cameraX: this.cameraX,
      cameraY: this.renderer.camera.position.y,
      zoom: this.zoom,
    });
  }

  get canvasEl(): HTMLCanvasElement { return this.canvas; }

  private renderCeilings(lvl: LevelData, color: Color): void {
    const r = this.renderer;
    const offsets = this.segmentOffsets(lvl);
    const step = 4; // world units
    for (let i = 0; i < lvl.segments.length; i++) {
      const seg = lvl.segments[i];
      if (!seg.terrain.ceiling) continue;
      const segStart = offsets[i];
      const segEnd = segStart + seg.length;
      const viewLeft = Math.max(segStart, this.cameraX);
      const viewRight = Math.min(segEnd, this.cameraX + this.viewWidth);
      if (viewRight <= viewLeft) continue;
      const lineColor: Color = { ...color, a: 0.7 };
      let prevX = viewLeft;
      let prevY = this.sampleCeiling(seg, prevX - segStart);
      for (let x = viewLeft + step; x <= viewRight; x += step) {
        const y = this.sampleCeiling(seg, x - segStart);
        r.drawLine(prevX, 80 + prevY, x, 80 + y, 1.5, lineColor);
        prevX = x; prevY = y;
      }
    }
  }

  private sampleCeiling(seg: LevelData['segments'][0], localX: number): number {
    const c = seg.terrain.ceiling;
    if (!c) return 0;
    const amp = c.amplitude ?? 0;
    const freq = c.frequency ?? 0.01;
    const phase = c.phase ?? 0;
    switch (c.function) {
      case 'sin': return c.offset + amp * Math.sin(freq * localX + phase);
      case 'cos': return c.offset + amp * Math.cos(freq * localX + phase);
      case 'tan': {
        const raw = Math.tan(freq * localX + phase);
        return c.offset + amp * Math.max(-3, Math.min(3, raw));
      }
      default: return c.offset;
    }
  }
}

function triggerColor(kind: string): Color {
  switch (kind) {
    case 'text':        return { r: 1,   g: 1,   b: 0.5, a: 1 };
    case 'color':       return { r: 1,   g: 0.6, b: 0.2, a: 1 };
    case 'speed':       return { r: 0.6, g: 1,   b: 0.4, a: 1 };
    case 'camera':      return { r: 0.8, g: 0.4, b: 1,   a: 1 };
    case 'music':       return { r: 1,   g: 0.3, b: 0.7, a: 1 };
    case 'gravity':     return { r: 0.5, g: 0.8, b: 1,   a: 1 };
    case 'player_mode': return { r: 0.4, g: 1,   b: 1,   a: 1 };
    case 'spawn':       return { r: 1,   g: 0.5, b: 0.5, a: 1 };
    case 'mode':        return { r: 1,   g: 0.7, b: 0,   a: 1 };
    case 'fog':         return { r: 0.6, g: 0.6, b: 0.7, a: 1 };
    default:            return { r: 0.8, g: 0.8, b: 0.8, a: 1 };
  }
}
